import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { RoomService } from '../room.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { IRoom } from '../iroom';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { debounceTime, map, Observable, startWith, switchMap, Subject, take, takeUntil, tap } from 'rxjs';
import { RoomTypeDetailComponent } from '../../room-type/room-type-detail/room-type-detail.component';
import { RoomStatus, RoomStatusLabels } from 'src/app/common/enums/room-status';
import { ValidationService } from 'src/app/common/validation/validation.service';
import { CapitalizeFirstLetterDirective } from 'src/app/common/directive/capitalize-first-letter.directive';
@Component({
  selector: 'app-room-detail',
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    ReactiveFormsModule,
    CapitalizeFirstLetterDirective
  ],
  templateUrl: './room-detail.component.html',
  styleUrl: './room-detail.component.scss'
})
export class RoomDetailComponent implements OnInit, OnDestroy {

  roomForm: FormGroup;
  isEdit: boolean;

  roomTypeddl: CommonDropDown[] = [];
  filteredRoomType$: Observable<CommonDropDown[]>;
  roomTypeCtrl = new FormControl();
  showAddOptionforRoomType = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  public roomStatusData = RoomStatusLabels;
  public roomstatusList = Object.values(RoomStatus).filter(value => typeof value === 'number');

  constructor(
    private _roomService: RoomService,
    private _formbuilder: FormBuilder,
    private dialogRef: MatDialogRef<RoomDetailComponent>,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private _lookupService: LookupService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private _data: { room?: IRoom, isEdit?: boolean, roomTypeId?: number, wardId?: number },
  ) {
    this.isEdit = _data?.isEdit ?? false;
  }

  ngOnInit(): void {
    const wardId = this._data?.wardId;
    this._lookupService.getRoomTypes(undefined, wardId).pipe(takeUntil(this._unsubscribeAll)).subscribe((data) => {
      this.roomTypeddl = data || [];
      this.filteredRoomType$ = this.roomTypeCtrl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        switchMap(value => {
          const search = typeof value === 'string' ? value : value?.name || '';
          return this._lookupService.getRoomTypes(search, wardId).pipe(
            tap(list => {
              this.showAddOptionforRoomType =
                !!search && !list.some(x => x.name.toLowerCase() === search.toLowerCase());
            }),
            map(list => list)
          );
        })
      );
      if (this.isEdit && this._data?.room?.roomTypeId) {
        const selected = this.roomTypeddl.find(x => x.id === this._data?.room?.roomTypeId);
        if (selected) {
          this.roomTypeCtrl.setValue(selected);
        }
      }
    });

    const initialRoomTypeId = this.isEdit ? this._data?.room?.roomTypeId : (this._data?.roomTypeId || '');
    this.roomForm = this._formbuilder.group({
      roomTypeId: [initialRoomTypeId],
      floor: [this._data?.room?.floor || '', [Validators.maxLength(5), ValidationService.numericValidator]],
      roomNumber: [this._data?.room?.roomNumber || '', [Validators.maxLength(5)]],
      status: [RoomStatus[this._data?.room?.status as any as keyof typeof RoomStatus] ?? RoomStatus.Available],
      isActive: [this._data?.room?.isActive ?? true]
    })

    if (initialRoomTypeId) {
      this.roomForm.get('roomTypeId')?.disable();
      this.roomForm.get('status')?.disable();
    } else {
      this.roomForm.get('roomTypeId')?.enable();
      this.roomForm.get('status')?.enable();
    }
  }

  onAddRoom() {
    const data = this.roomForm.getRawValue();
    this._roomService.createRoom(data).subscribe((response) => {
      if (response.isSuccess) {
        this.dialogRef.close(response);
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.ROOM')));
      }
      else {
        const allErrors = Object.values(response.errors).flat();
        this.toastr.error(allErrors.join("\n"));
      }
    });
  }

  onUpdateRoom() {
    const data = {
      id: this._data?.room?.id,
      ...this.roomForm.getRawValue()
    };

    this._roomService.updateRoom(data).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.dialogRef.close(response);
          this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.ROOM')));
        }
      },
      error: (error) => {
        if (error?.error?.errors) {
          const allErrors = Object.values(error.error.errors).flat();
          this.toastr.error(allErrors.join("\n"));
        } else {
          this.toastr.error('Something went wrong');
        }
      }
    });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  displayroomType(roomType: CommonDropDown): string {
    return roomType && roomType.name ? roomType.name : '';
  }

  onRoomTypeSelected(roomType: CommonDropDown) {
    this.roomForm.patchValue({
      roomTypeId: roomType.id
    });
    this.roomTypeCtrl.setValue(roomType);
  }

  addRoomType(name: string) {
    const dialogRef = this.dialog.open(RoomTypeDetailComponent, {
      width: '70vw',
      data: {
        roomtype: { name: name },
        isEdit: false
      }
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (!response?.result) return;
      // refresh lookup
      const wardId = this._data?.wardId;
      this._lookupService.getRoomTypes(undefined, wardId).pipe(take(1)).subscribe(list => {
        this.roomTypeddl = list;
        const newRoomType = list.find(x => x.id === response.result.id);
        // set in dropdown
        this.roomTypeCtrl.setValue(newRoomType);
        // set roomTypeId in form
        this.roomForm.patchValue({
          roomTypeId: newRoomType?.id
        });
      });
    });
  }


  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

}
